/**
 * Quiz Metadata Utilities
 *
 * Extract geography and flag information from quiz configurations.
 */

import { getCountryFlag, getFlagFromLabel } from "./countryFlags";
import { geojsonDatasets } from "@/data/geojsonDatasets";
import type { QuizConfig } from "@/db/types";

/**
 * Extract geography (country name) from a quiz config
 */
export function getQuizGeography(quizConfig: QuizConfig): string | null {
  // Try to match dataset source path with catalogue
  const datasetSource = quizConfig.datasetSource;
  if (datasetSource?.type === "dataset-url") {
    const dataset = geojsonDatasets.find(
      (d) => d.path === datasetSource.path
    );
    if (dataset) {
      return dataset.geography;
    }
  }

  // Try to parse from dataset label (e.g., "France · Departments")
  if (quizConfig.datasetLabel) {
    const segments = quizConfig.datasetLabel
      .split("·")
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0);
    if (segments.length > 0 && segments[0]) {
      return segments[0];
    }
  }

  return null;
}

/**
 * Get flag emoji for a quiz config
 */
export function getQuizFlag(quizConfig: QuizConfig): string {
  const geography = getQuizGeography(quizConfig);
  if (geography) {
    return getCountryFlag(geography);
  }

  // Fallback: try label parsing
  if (quizConfig.datasetLabel) {
    return getFlagFromLabel(quizConfig.datasetLabel);
  }

  return "🗺️"; // Unknown flag
}

/**
 * Get geography from journey (uses first quiz's geography)
 */
export function getJourneyGeography(
  journey: { quizzes: Array<{ quizId: string }> },
  quizConfigs: QuizConfig[]
): string | null {
  if (journey.quizzes.length === 0) return null;

  const firstQuizId = journey.quizzes[0]?.quizId;
  if (!firstQuizId) return null;

  const firstQuiz = quizConfigs.find((q) => q.signature === firstQuizId);

  if (firstQuiz) {
    return getQuizGeography(firstQuiz);
  }

  return null;
}

/**
 * Get flag for journey
 */
export function getJourneyFlag(
  journey: { quizzes: Array<{ quizId: string }> },
  quizConfigs: QuizConfig[]
): string {
  const geography = getJourneyGeography(journey, quizConfigs);
  if (geography) {
    return getCountryFlag(geography);
  }
  return "🗺️";
}
